import {useState, useEffect, useCallback, useRef} from 'react';
import useEmblaCarousel, {EmblaOptionsType} from 'embla-carousel-react';
import {WheelGesturesPlugin} from 'embla-carousel-wheel-gestures';
import {flushSync} from 'react-dom';

//Hooks
import useRecursiveTimeout from './useRecursiveTimeout';

//Utils
import {numberWithinRange} from './embla-utils';

//Types
import {InitialArrowActiveState} from './types';

interface CarouselOptions {
  emblaOptions?: EmblaOptionsType;
  initialArrowsActiveState?: {
    leftArrowIsActive: boolean;
    rightArrowIsActive: boolean;
  };
  autoPlayTime?: number; // autoplay time in mill seconds
  shouldAutoplay?: boolean;
  stopAutoplayAfterArrowClick?: boolean;
  tweenFactor?: number;
  isScroll?: boolean;
}

const AUTO_PLAY_INTERVAL = 4000;
const TWEEN_FACTOR = 0.2;
export const defaultCarouselOptions: CarouselOptions = {
  emblaOptions: {
    containScroll: 'trimSnaps',
    dragFree: true,
    loop: false,
  },
  initialArrowsActiveState: {
    leftArrowIsActive: false,
    rightArrowIsActive: false,
  },
  autoPlayTime: AUTO_PLAY_INTERVAL,
  shouldAutoplay: false,
  /**
   * @Note
   * if product would want to stop auto play on click
   */
  stopAutoplayAfterArrowClick: false,
  tweenFactor: TWEEN_FACTOR,
  /**
   * @Note
   * if the needs come in future, we can implement mutiple animation tween support via
   * introudting seperatly a tween object defining its nature
   */
  isScroll: false,
};

type Axis = 'x' | 'y';

export default function useCarousel(carouselOptions: CarouselOptions = defaultCarouselOptions) {
  const [axis] = useState<Axis>('x');
  const [forceWheelAxis] = useState<Axis | undefined>(axis);
  const [target] = useState<Element | undefined>();
  const [tweenValues, setTweenValues] = useState<number[]>([]);

  const tweenFactor = useRef(0);

  const {
    emblaOptions = defaultCarouselOptions.emblaOptions,
    initialArrowsActiveState = defaultCarouselOptions.initialArrowsActiveState,
    autoPlayTime = defaultCarouselOptions.autoPlayTime,
    shouldAutoplay = defaultCarouselOptions.shouldAutoplay,
    stopAutoplayAfterArrowClick = defaultCarouselOptions.stopAutoplayAfterArrowClick,
    // tweenFactor,
    isScroll,
  } = carouselOptions;

  const emblaOptionsToPassToEmblaFactoryConstructor = {
    ...defaultCarouselOptions.emblaOptions,
    ...emblaOptions,
  };

  const [emblaRef, emblaApi] = useEmblaCarousel(emblaOptionsToPassToEmblaFactoryConstructor, [
    WheelGesturesPlugin({
      forceWheelAxis,
      target,
    }),
  ]);

  const [arrowsState, setArrowsState] = useState(initialArrowsActiveState);

  const setTweenFactor = useCallback(emblaApi => {
    tweenFactor.current = TWEEN_FACTOR * emblaApi.scrollSnapList().length;
  }, []);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setArrowsState({
      leftArrowIsActive: emblaApi?.canScrollPrev(),
      rightArrowIsActive: emblaApi?.canScrollNext(),
    });
  }, [emblaApi]);

  const onScroll = useCallback(() => {
    if (!emblaApi) return;

    const engine = emblaApi.internalEngine();
    const scrollProgress = emblaApi.scrollProgress();

    const styles = emblaApi.scrollSnapList().map((scrollSnap, index) => {
      let diffToTarget = scrollSnap - scrollProgress;

      if (engine.options.loop) {
        engine.slideLooper.loopPoints.forEach(loopItem => {
          const target = loopItem.target();
          if (index === loopItem.index && target !== 0) {
            const sign = Math.sign(target);
            if (sign === -1) diffToTarget = scrollSnap - (1 + scrollProgress);
            if (sign === 1) diffToTarget = scrollSnap + (1 - scrollProgress);
          }
        });
      }
      const tweenValue = 1 - Math.abs(diffToTarget * tweenFactor.current);
      return numberWithinRange(tweenValue, 0, 1);
    });
    setTweenValues(styles);
  }, [emblaApi, setTweenValues]);

  useEffect(() => {
    if (!emblaApi) return;

    if (isScroll) {
      setTweenFactor(emblaApi);
      onScroll();
      emblaApi.on('scroll', () => {
        flushSync(() => onScroll());
      });
      emblaApi.on('reInit', onScroll);
    }

    onSelect();
    emblaApi.on('select', onSelect);
  }, [emblaApi, isScroll]);

  const autoplay = useCallback(() => {
    if (!emblaApi) return;
    if (emblaApi.canScrollNext()) {
      emblaApi.scrollNext();
    } else {
      emblaApi.scrollTo(0);
    }
  }, [emblaApi]);

  const {play, stop} = useRecursiveTimeout(autoplay, autoPlayTime);

  useEffect(() => {
    return () => {
      if (emblaApi) {
        emblaApi.destroy();
      }
    };
  }, []);

  const scrollNext = useCallback(() => {
    if (!emblaApi) {
      return;
    }

    emblaApi.scrollNext();

    if (shouldAutoplay && stopAutoplayAfterArrowClick) {
      stop();
    }
  }, [emblaApi, shouldAutoplay, stop]);

  const scrollPrevious = useCallback(() => {
    if (!emblaApi) {
      return;
    }

    emblaApi.scrollPrev();

    if (shouldAutoplay && stopAutoplayAfterArrowClick) {
      stop();
    }
  }, [emblaApi, shouldAutoplay, stop]);

  useEffect(() => {
    if (shouldAutoplay) {
      play();
    }
  }, [play, shouldAutoplay]);

  return {
    emblaApi,
    emblaRef,
    scrollPrevious,
    scrollNext,
    onSelect,
    tweenValues,
    ...arrowsState,
  };
}
